import {
  isTextNormalizedEffectControlProp,
  normalizeTextEffectControl,
} from './textEffectControls'

export const TEXT_MOTION_PRESET_VERSION = 1
export const TEXT_MOTION_USER_PRESETS_KEY = 'vyby/text/motionPresets/v1'
export const TEXT_MOTION_PRESET_SOURCE_GLOBAL = 'global'
export const TEXT_MOTION_PRESET_SOURCE_USER = 'user'

export const TEXT_MOTION_PARTS = ['start', 'left', 'right', 'end']

export const TEXT_MOTION_BASE_PROPS = [
  'position.x',
  'position.y',
  'scale.x',
  'scale.y',
  'rotation.z',
]

export const TEXT_MOTION_SMART_PROPS = [
  ...TEXT_MOTION_BASE_PROPS,
  'opacity',
]

export const TEXT_MOTION_STYLE_PROPS = [
  'font.family',
  'font.size',
  'font.weight',
  'font.style',
  'font.color',
  'font.lineHeight',
  'font.letterSpacing',
  'font.align',
  'effect.outlineWidth',
  'effect.outlineColor',
  'effect.shadowColor',
  'effect.shadowBlur',
  'effect.shadowOffsetX',
  'effect.shadowOffsetY',
]

const finiteNumber = (value, fallback = 0) => {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

const finiteNonNegative = (value, fallback = 0) =>
  Math.max(0, finiteNumber(value, fallback))

const roundNumber = (value, decimals = 1) => {
  const next = Number(value)
  if (!Number.isFinite(next)) return value
  const factor = 10 ** decimals
  const rounded = Math.round(next * factor) / factor
  return Math.abs(rounded) < 1e-9 ? 0 : rounded
}

const roundTextMotionValue = (value, decimals = 1) => {
  if (typeof value === 'number') return roundNumber(value, decimals)
  return value
}

export const roundTextMotionPropValue = (prop, value) => {
  if (prop.startsWith('position.')) return roundTextMotionValue(value, 1)
  if (prop.startsWith('scale.')) return roundTextMotionValue(value, 2)
  if (prop.startsWith('rotation.')) return roundTextMotionValue(value, 2)
  if (prop === 'opacity') return roundTextMotionValue(value, 3)
  return roundTextMotionValue(value, 3)
}

const roundJsonNumbers = (value, decimals = 3) => {
  if (typeof value === 'number') return roundNumber(value, decimals)
  if (Array.isArray(value)) return value.map((item) => roundJsonNumbers(item, decimals))
  if (!value || typeof value !== 'object') return value
  return Object.entries(value).reduce((acc, [key, next]) => {
    acc[key] = roundJsonNumbers(next, decimals)
    return acc
  }, {})
}

const roundTextMotionSmartGroup = (prop, group = {}) => {
  if (!group || typeof group !== 'object') return group
  const next = roundJsonNumbers(group, 3)
  ;['start', 'left', 'right', 'end'].forEach((part) => {
    if (next[part] && typeof next[part] === 'object' && 'value' in next[part]) {
      next[part].value = roundTextMotionPropValue(prop, next[part].value)
    }
  })
  if (next.offsets && typeof next.offsets === 'object') {
    next.offsets = {
      ...next.offsets,
      entry: roundTextMotionValue(next.offsets.entry, 3),
      exit: roundTextMotionValue(next.offsets.exit, 3),
    }
  }
  return next
}

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, next]) => {
    if (typeof next !== 'undefined') acc[key] = next
    return acc
  }, {})

const isTextMotionMicroActive = (micro = {}) => {
  if (!micro || typeof micro !== 'object') return false
  const low = finiteNumber(micro.window?.low, 0)
  const high = finiteNumber(micro.window?.high, 0)
  const radius = finiteNumber(micro.radius, 0)
  return Math.abs(low) > 1e-6 || Math.abs(high) > 1e-6 || Math.abs(radius) > 1e-6
}

const normalizeTextMotionMicroRegions = (regions = {}) => {
  if (!regions || typeof regions !== 'object') return {}
  return ['entry', 'sustain', 'exit'].reduce((acc, region) => {
    const micro = regions[region]
    if (!isTextMotionMicroActive(micro)) return acc
    const cloned = roundJsonNumbers(cloneJson(micro), 3)
    if (cloned) acc[region] = cloned
    return acc
  }, {})
}

export const textMotionPresetValue = (source, id) => `${source}:${id}`

export const parseTextMotionPresetValue = (value) => {
  const raw = `${value || ''}`.trim()
  const match = raw.match(/^(global|user):(.+)$/)
  if (!match) return { source: '', id: '' }
  return {
    source: match[1],
    id: match[2],
  }
}

export const textMotionPropDefault = (prop) =>
  prop === 'opacity' || prop === 'scale.x' || prop === 'scale.y' ? 1 : 0

const normalizeTextMotionScaleUnit = (scaleUnit, parts = {}) => {
  if (scaleUnit === 'raw' || scaleUnit === 'percent') return scaleUnit
  const values = TEXT_MOTION_PARTS.flatMap((part) => [
    Number(parts?.[part]?.scale?.x),
    Number(parts?.[part]?.scale?.y),
  ]).filter(Number.isFinite)
  return values.some((value) => Math.abs(value) > 10) ? 'percent' : 'raw'
}

export const seedTextMotionSmartGroup = (propOrValue = 0) => {
  const val =
    typeof propOrValue === 'string' ? textMotionPropDefault(propOrValue) : propOrValue
  return {
    start: { value: val },
    left: { value: val },
    right: { value: val },
    end: { value: val },
    offsets: { entry: 0, exit: 0 },
    microByRegion: {},
  }
}

export const normalizeTextMotionPresetParts = (parts = {}, scaleUnit = 'percent') =>
  TEXT_MOTION_PARTS.reduce((acc, part) => {
    const source = parts?.[part] || {}
    const scaleFallback = scaleUnit === 'raw' ? 1 : 100
    acc[part] = {
      position: {
        x: roundTextMotionPropValue('position.x', finiteNumber(source.position?.x, 0)),
        y: roundTextMotionPropValue('position.y', finiteNumber(source.position?.y, 0)),
      },
      scale: {
        x: roundTextMotionPropValue('scale.x', finiteNumber(source.scale?.x, scaleFallback)),
        y: roundTextMotionPropValue('scale.y', finiteNumber(source.scale?.y, scaleFallback)),
      },
      rotation: {
        z: roundTextMotionPropValue('rotation.z', finiteNumber(source.rotation?.z, 0)),
      },
    }
    return acc
  }, {})

export const normalizeTextMotionPresetOffsets = (offsets = {}) => {
  if (!offsets || typeof offsets !== 'object') return null
  return {
    entry: roundTextMotionValue(finiteNonNegative(offsets.entry, 0), 3),
    exit: roundTextMotionValue(finiteNonNegative(offsets.exit, 0), 3),
  }
}

export const extractTextMotionPresetOffsets = (smartKeys = {}) => {
  const offsets = smartKeys?.['position.x']?.offsets || null
  return normalizeTextMotionPresetOffsets(offsets)
}

export const extractTextMotionPresetMicroByRegion = (smartKeys = {}) =>
  TEXT_MOTION_SMART_PROPS.reduce((acc, prop) => {
    const microByRegion = smartKeys?.[prop]?.microByRegion
    if (!microByRegion || typeof microByRegion !== 'object') return acc
    const regions = normalizeTextMotionMicroRegions(microByRegion)
    if (Object.keys(regions).length) acc[prop] = regions
    return acc
  }, {})

export const normalizeTextMotionPresetMicroByRegion = (microByRegion = {}) => {
  if (!microByRegion || typeof microByRegion !== 'object') return {}
  return TEXT_MOTION_SMART_PROPS.reduce((acc, prop) => {
    const source = microByRegion[prop]
    if (!source || typeof source !== 'object') return acc
    const regions = normalizeTextMotionMicroRegions(source)
    if (Object.keys(regions).length) acc[prop] = regions
    return acc
  }, {})
}

export const normalizeTextMotionStyleDefaults = (styleDefaults = {}) =>
  TEXT_MOTION_STYLE_PROPS.reduce((acc, prop) => {
    if (typeof styleDefaults?.[prop] === 'undefined') return acc
    acc[prop] = isTextNormalizedEffectControlProp(prop)
      ? normalizeTextEffectControl(prop, styleDefaults[prop])
      : styleDefaults[prop]
    return acc
  }, {})

export const normalizeTextMotionPresetProps = (props = {}) => {
  if (!props || typeof props !== 'object') return {}
  return TEXT_MOTION_SMART_PROPS.reduce((acc, prop) => {
    const group = props[prop]
    if (!group || typeof group !== 'object') return acc
    const cloned = roundTextMotionSmartGroup(prop, cloneJson(group))
    if (cloned) acc[prop] = cloned
    return acc
  }, {})
}

export const buildTextMotionPresetPayload = ({
  parts = {},
  scaleUnit = null,
  props = {},
  offsets = null,
  smartKeys = {},
  microByRegion = null,
  styleDefaults = {},
  supportedProps = TEXT_MOTION_SMART_PROPS,
} = {}) => {
  const normalizedOffsets = normalizeTextMotionPresetOffsets(
    offsets || extractTextMotionPresetOffsets(smartKeys)
  )
  const normalizedScaleUnit = normalizeTextMotionScaleUnit(scaleUnit, parts)
  const normalizedMicroByRegion = normalizeTextMotionPresetMicroByRegion(
    microByRegion || extractTextMotionPresetMicroByRegion(smartKeys)
  )
  const normalizedProps = normalizeTextMotionPresetProps(props)
  const normalizedStyleDefaults = normalizeTextMotionStyleDefaults(styleDefaults)

  return compactObject({
    version: TEXT_MOTION_PRESET_VERSION,
    scaleUnit: normalizedScaleUnit,
    supportedProps: supportedProps.filter((prop) => TEXT_MOTION_SMART_PROPS.includes(prop)),
    parts: normalizeTextMotionPresetParts(parts, normalizedScaleUnit),
    props: Object.keys(normalizedProps).length ? normalizedProps : undefined,
    offsets: normalizedOffsets || undefined,
    microByRegion: Object.keys(normalizedMicroByRegion).length
      ? normalizedMicroByRegion
      : undefined,
    styleDefaults: Object.keys(normalizedStyleDefaults).length
      ? normalizedStyleDefaults
      : undefined,
  })
}

export const createTextMotionUserPreset = ({
  id,
  name,
  createdAt = Date.now(),
  ...payloadInput
} = {}) => ({
  id,
  name,
  source: TEXT_MOTION_PRESET_SOURCE_USER,
  createdAt,
  ...buildTextMotionPresetPayload(payloadInput),
})

export const createTextMotionGlobalPresetExport = ({
  id,
  name,
  description = '',
  intentTags = [],
  ...payloadInput
} = {}) => ({
  id,
  name,
  source: TEXT_MOTION_PRESET_SOURCE_GLOBAL,
  description,
  intentTags: Array.isArray(intentTags) ? intentTags.filter(Boolean) : [],
  ...buildTextMotionPresetPayload(payloadInput),
})

export const normalizeTextMotionPreset = (preset = {}) => {
  if (!preset || typeof preset !== 'object' || !preset.parts) return null
  return {
    ...preset,
    ...buildTextMotionPresetPayload(preset),
    source: preset.source || TEXT_MOTION_PRESET_SOURCE_USER,
  }
}

export const applyTextMotionPresetToModal = (
  preset,
  {
    smartKeys = {},
    setPartValue,
    setScalePercentValue,
    setTimingOffsetSeconds,
    applyStyleDefaults,
    upsertSmartKeys,
    seedGroup = seedTextMotionSmartGroup,
  } = {}
) => {
  const normalized = normalizeTextMotionPreset(preset)
  if (!normalized || typeof setPartValue !== 'function') return false
  const supportedProps = Array.isArray(normalized.supportedProps)
    ? normalized.supportedProps
    : TEXT_MOTION_SMART_PROPS
  const scaleUnit = normalized.scaleUnit === 'raw' ? 'raw' : 'percent'

  TEXT_MOTION_PARTS.forEach((part) => {
    const pt = normalized.parts?.[part]
    if (!pt) return
    if (supportedProps.includes('position.x')) setPartValue('position.x', part, pt.position?.x)
    if (supportedProps.includes('position.y')) setPartValue('position.y', part, pt.position?.y)
    if (supportedProps.includes('scale.x')) {
      if (scaleUnit === 'percent' && typeof setScalePercentValue === 'function')
        setScalePercentValue('scale.x', part, pt.scale?.x)
      else setPartValue('scale.x', part, pt.scale?.x)
    }
    if (supportedProps.includes('scale.y')) {
      if (scaleUnit === 'percent' && typeof setScalePercentValue === 'function')
        setScalePercentValue('scale.y', part, pt.scale?.y)
      else setPartValue('scale.y', part, pt.scale?.y)
    }
    if (supportedProps.includes('rotation.z')) setPartValue('rotation.z', part, pt.rotation?.z)
  })

  if (normalized.offsets && typeof setTimingOffsetSeconds === 'function') {
    setTimingOffsetSeconds('entry', normalized.offsets.entry)
    setTimingOffsetSeconds('exit', normalized.offsets.exit)
  }

  if (typeof upsertSmartKeys === 'function' && normalized.microByRegion) {
    Object.entries(normalized.microByRegion).forEach(([prop, microByRegion]) => {
      if (!TEXT_MOTION_SMART_PROPS.includes(prop) || !supportedProps.includes(prop)) return
      const group = {
        ...seedGroup(prop),
        ...(smartKeys?.[prop] || {}),
        microByRegion: {
          ...(smartKeys?.[prop]?.microByRegion || {}),
          ...cloneJson(microByRegion),
        },
      }
      upsertSmartKeys(prop, group)
    })
  }

  if (typeof upsertSmartKeys === 'function' && normalized.props) {
    Object.entries(normalized.props).forEach(([prop, group]) => {
      if (!TEXT_MOTION_SMART_PROPS.includes(prop) || !supportedProps.includes(prop)) return
      upsertSmartKeys(prop, {
        ...seedGroup(prop),
        ...(smartKeys?.[prop] || {}),
        ...cloneJson(group),
      })
    })
  }

  if (typeof applyStyleDefaults === 'function' && normalized.styleDefaults) {
    applyStyleDefaults(normalized.styleDefaults)
  }

  return true
}
